/**
 * 회의록 내려받기 — §11.4.
 *
 * **인가와 가시성 판정은 서버에 있다.** 여기서 하는 일은 요청을 보내고
 * 받은 바이트를 파일로 떨구는 것뿐이다. 화면이 받아 놓고 거르는 방식은
 * 쓰지 않는다 — 바이트가 브라우저에 도착한 시점에 이미 누출이기 때문이다.
 * 뷰어가 호스트 전용 내용을 못 받는 것은 서버의 vis 필터가 보장한다
 * (server/export.py `collect` → `filter_for_role`).
 *
 * 응답이 JSON 이 아니라 파일이라 `api.ts` 의 `request()` 를 쓸 수 없다
 * (그쪽은 본문을 JSON 으로 파싱한다). 대신 오류 형태(`{detail}`)와 사용자
 * 문구 규약은 `ApiError` 로 맞춘다 — 화면이 오류를 한 방식으로 다룬다.
 */

import { API_BASE } from './config'
import { ApiError } from './api'

/** 형식 — 참가자 기본은 `txt`(받아서 바로 여는 형식). */
export type ExportFormat = 'txt' | 'md' | 'json'

export const EXPORT_FORMATS: readonly ExportFormat[] = ['txt', 'md', 'json']

export const FORMAT_LABELS: Record<ExportFormat, string> = {
  txt: '텍스트 (.txt)',
  md: '마크다운 (.md)',
  json: 'JSON (.json)',
}

/**
 * 기록 미보관 방의 경계 문구 — **서버 `PARTIAL_NOTE` 와 같은 문장**이다.
 * 파일 머리말과 화면이 다른 말을 하면 사용자가 두 개의 약속을 읽게 된다.
 */
export const PARTIAL_NOTE = '기록 미보관 방: 현재 보관 중인 자막만 저장됩니다.'

export interface TranscriptFile {
  blob: Blob
  filename: string
  /** 회의 전체가 아닐 수 있는가(서버 `X-Transcript-Partial`) */
  partial: boolean
  /** 담긴 항목 수(서버 `X-Transcript-Count`) */
  count: number
}

/** `attachment; filename="…"` 에서 파일명을 꺼낸다. 실패하면 빈 문자열. */
export function parseFilename(disposition: string | null): string {
  if (!disposition) return ''
  const quoted = /filename="([^"]+)"/.exec(disposition)
  if (quoted?.[1]) return quoted[1]
  const bare = /filename=([^;]+)/.exec(disposition)
  return bare?.[1]?.trim() ?? ''
}

/** 서버가 파일명을 안 줬을 때의 대비책 — 방과 날짜면 구분에 충분하다. */
export function fallbackFilename(room: string, fmt: ExportFormat): string {
  const safe = room.replace(/[^A-Za-z0-9_-]/g, '') || 'room'
  const d = new Date()
  const p = (n: number): string => String(n).padStart(2, '0')
  const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`
  return `omnitrans-${safe}-${stamp}.${fmt}`
}

/**
 * 회의록을 서버에서 받아 온다(`GET /api/rooms/{room}/export`).
 *
 * `key` 는 host_key 또는 view_key 다 — **서버가 이 키로 범위를 정한다**.
 * 익명 패스코드 뷰어도 자기 view_key 로 같은 경로를 쓴다(§11.4 참가자 권리).
 */
export async function fetchTranscript(
  room: string,
  key: string,
  fmt: ExportFormat = 'txt',
): Promise<TranscriptFile> {
  const res = await fetch(
    `${API_BASE}/rooms/${encodeURIComponent(room)}/export?fmt=${fmt}`,
    {
      credentials: 'same-origin',
      headers: { Authorization: `Bearer ${key}` },
    },
  )
  if (!res.ok) {
    // 오류 본문은 JSON(`{detail}`)이다 — 파일 응답과 형태가 다르다.
    let code = 'unknown'
    try {
      const body = (await res.json()) as { detail?: unknown }
      if (typeof body.detail === 'string') code = body.detail
    } catch {
      /* 본문이 없거나 JSON 이 아니면 코드 없이 상태만 남긴다 */
    }
    throw new ApiError(
      res.status,
      code,
      res.status === 403
        ? '회의록을 받을 권한이 없습니다.'
        : '회의록을 받지 못했습니다. 잠시 후 다시 시도해 주세요.',
    )
  }
  const blob = await res.blob()
  return {
    blob,
    filename:
      parseFilename(res.headers.get('Content-Disposition')) || fallbackFilename(room, fmt),
    partial: res.headers.get('X-Transcript-Partial') === '1',
    count: Number(res.headers.get('X-Transcript-Count') ?? 0),
  }
}

/**
 * Blob 을 파일로 저장시킨다.
 *
 * objectURL 은 **반드시 되돌려준다**(revoke). 회의 중 여러 번 받으면 안
 * 돌려준 URL 이 blob 을 통째로 붙잡아 탭 메모리가 계속 자란다.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
  // 즉시 revoke 하면 일부 브라우저에서 저장이 취소된다 — 다음 틱에 돌려준다.
  // 회수는 **정리 작업**이라, 실패해도 저장 자체를 깨뜨리면 안 된다
  // (지연 실행이라 이 시점엔 이미 페이지가 바뀌었을 수도 있다).
  setTimeout(() => {
    try {
      URL.revokeObjectURL(url)
    } catch {
      /* 회수 실패는 무시 — 탭이 닫히면 어차피 함께 사라진다 */
    }
  }, 0)
}

/** 받아서 저장까지 — 화면은 이 함수 하나만 부른다. */
export async function downloadTranscript(
  room: string,
  key: string,
  fmt: ExportFormat = 'txt',
): Promise<TranscriptFile> {
  const file = await fetchTranscript(room, key, fmt)
  saveBlob(file.blob, file.filename)
  return file
}
